import { useEffect, useRef, useState } from 'react';
import { motion } from 'framer-motion';
import { REEL_STRIP, SYMBOLS } from './game';

const STOP_TIMES = [900, 1325, 1800];

const SpinDecision = ({ game, preview, onNudge, onLock }) => {
  const activeReels =
    game.lastNudgedIndex === null ? [0, 1, 2] : [game.lastNudgedIndex];
  const [display, setDisplay] = useState(() =>
    game.reels.map((symbol, index) =>
      activeReels.includes(index) ? null : symbol
    )
  );
  const [stopped, setStopped] = useState(() =>
    game.reels.map((_, index) => !activeReels.includes(index))
  );
  const [countedPayout, setCountedPayout] = useState(0);
  const intervals = useRef([]);
  const timers = useRef([]);
  const reduceMotion = !!window.matchMedia?.('(prefers-reduced-motion: reduce)')
    .matches;
  const revealed = stopped.every(Boolean);

  const randomSymbol = () =>
    REEL_STRIP[Math.floor(Math.random() * REEL_STRIP.length)];

  const revealAll = () => {
    intervals.current.forEach(clearInterval);
    timers.current.forEach(clearTimeout);
    intervals.current = [];
    timers.current = [];
    setDisplay([...game.reels]);
    setStopped([true, true, true]);
  };

  useEffect(() => {
    if (reduceMotion) {
      revealAll();
      return undefined;
    }

    activeReels.forEach((index, activeIndex) => {
      intervals.current.push(
        setInterval(() => {
          setDisplay((current) =>
            current.map((symbol, reelIndex) =>
              reelIndex === index ? randomSymbol() : symbol
            )
          );
        }, 65 + index * 18)
      );
      const stopTime =
        game.lastNudgedIndex === null
          ? STOP_TIMES[index]
          : 850 + activeIndex * 200;
      timers.current.push(
        setTimeout(() => {
          clearInterval(intervals.current[activeIndex]);
          setDisplay((current) =>
            current.map((symbol, reelIndex) =>
              reelIndex === index ? game.reels[index] : symbol
            )
          );
          setStopped((current) =>
            current.map((value, reelIndex) =>
              reelIndex === index ? true : value
            )
          );
        }, stopTime)
      );
    });

    return () => {
      intervals.current.forEach(clearInterval);
      timers.current.forEach(clearTimeout);
    };
    // This component is remounted for each spin or nudge.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!revealed) return undefined;
    if (reduceMotion || preview.payout === 0) {
      setCountedPayout(preview.payout);
      return undefined;
    }
    setCountedPayout(0);
    const step = Math.max(1, Math.ceil(preview.payout / 30));
    const interval = setInterval(() => {
      setCountedPayout((current) => {
        if (current + step >= preview.payout) {
          clearInterval(interval);
          return preview.payout;
        }
        return current + step;
      });
    }, 24);
    return () => clearInterval(interval);
  }, [preview.payout, reduceMotion, revealed]);

  return (
    <section
      className={`slotPanel slotSpinDecision slotEvent-${preview.event}`}
    >
      <span className="slotEyebrow">
        SPIN {game.spinNumber} / {game.totalSpins}
      </span>
      <h2>{revealed ? 'The reels have spoken' : 'The machine is deciding…'}</h2>
      <span className="slotSrOnly" role="status">
        {revealed
          ? `Reels show ${game.reels
              .map((id) => SYMBOLS[id].name)
              .join(', ')}. Payout ${preview.payout}. HP loss ${
              preview.hpLoss
            }.`
          : 'Three reels are spinning.'}
      </span>

      <div className="slotMachineWindow" aria-hidden="true">
        <div className="slotPayline" />
        <div className="slotReels">
          {display.map((symbol, index) => (
            <motion.div
              key={index}
              className={`slotReel ${
                stopped[index] ? 'slotReelStopped' : 'slotReelMoving'
              } slotSymbol-${symbol || 'unknown'}`}
              animate={
                reduceMotion || stopped[index]
                  ? { y: 0, scale: stopped[index] ? [1.12, 0.94, 1] : 1 }
                  : {
                      y: [0, -9, 9, 0],
                      filter: ['blur(0px)', 'blur(2px)', 'blur(0px)'],
                    }
              }
              transition={
                stopped[index]
                  ? { duration: 0.36 }
                  : { duration: 0.22 + index * 0.04, repeat: Infinity }
              }
            >
              {symbol ? SYMBOLS[symbol].glyph : '?'}
            </motion.div>
          ))}
        </div>
      </div>

      {revealed ? (
        <>
          {preview.event !== 'normal' && (
            <motion.div
              className={`slotEventCallout slotEventCallout-${preview.event}`}
              initial={
                reduceMotion ? false : { scale: 0.4, opacity: 0, rotate: -5 }
              }
              animate={reduceMotion ? {} : { scale: 1, opacity: 1, rotate: 0 }}
            >
              {preview.event === 'jackpot' && 'JACKPOT!'}
              {preview.event === 'nearMiss' && 'SO CLOSE…'}
              {preview.event === 'bonus' && 'BONUS SPIN!'}
              {preview.event === 'doom' && 'THE MACHINE BITES'}
            </motion.div>
          )}
          <div className="slotEffectBursts">
            <motion.div
              className="slotEffectBurst"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <span>Symbol value</span>
              <strong>+{preview.base}</strong>
            </motion.div>
            {preview.effects.map((effect, index) => (
              <motion.div
                className={`slotEffectBurst slotEffect-${effect.kind}`}
                key={`${effect.label}-${index}`}
                initial={reduceMotion ? false : { opacity: 0, x: -16 }}
                animate={reduceMotion ? {} : { opacity: 1, x: 0 }}
                transition={{ delay: 0.12 + index * 0.1 }}
              >
                <span>{effect.label}</span>
                <strong>
                  {effect.amount > 0 ? '+' : ''}
                  {effect.amount}
                </strong>
              </motion.div>
            ))}
          </div>
          <div className="slotPreviewTotal">
            <span>Payout</span>
            <strong aria-label={`Payout ${preview.payout}`}>
              {countedPayout}
            </strong>
          </div>
          {preview.bonusSpins > 0 && (
            <p className="slotBonusNotice">
              +{preview.bonusSpins} bonus spin added to this run
            </p>
          )}
          <p className={preview.hpLoss > 0 ? 'slotDanger' : 'slotSafe'}>
            {preview.hpLoss > 0
              ? `The machine will take ${preview.hpLoss} HP.`
              : 'The machine takes no HP this spin.'}
          </p>
          <p className="slotHint">
            Spend one nudge to reroll a single reel, or accept the payout.
          </p>
          <div className="slotActions">
            {['left', 'middle', 'right'].map((label, index) => (
              <button
                className="slotSecondary"
                key={label}
                disabled={game.nudges === 0}
                onClick={() => onNudge(index)}
              >
                Nudge {label}
              </button>
            ))}
            <button className="slotPrimary" onClick={onLock}>
              Lock in payout →
            </button>
          </div>
        </>
      ) : (
        <button className="slotSkip" onClick={revealAll}>
          Stop the reels
        </button>
      )}
    </section>
  );
};

export default SpinDecision;
